import { useEffect, useMemo, useRef, useState } from "react"
import type { RefObject } from "react"
import { useField } from "formik"

import { ProductGroups } from "../../../../../modules/merchant/admin/products/productsTypes"

import { DeliveryTimesField, DynamicConfigKey } from "../form/formTypes"
import { MeetingServicePriceListState } from "../../createOrderTypes"

import {
    buildPriceMap,
    collectPriceChanges,
    collectSelectedLines,
    PriceChangeReason,
    updatePricesInDeliveries,
} from "./product/helpers"
import { PriceMismatchDialog } from "./product/PriceMismatchDialog"
import { CreditNoteOverlay } from "./product/CreditNoteOverlay"
import { ProductContainer } from "./product/ProductContainer"

import { ShopFiltersTypes } from "./ShopList"

type ShopListContentProps = Readonly<{
    productsList?: ProductGroups[]
    // Unfiltered catalog: the price comparison must not depend on search/favorite filters,
    // otherwise "update prices" would only touch the products currently visible.
    priceCatalogList?: ProductGroups[]
    filters: ShopFiltersTypes
    searchRegex?: RegExp
    orderId?: number
    isRefundOrder: boolean
    handleCloseMainDialog: () => void
    userId: number
    isEditMode?: boolean
    scrollContainerRef?: RefObject<HTMLDivElement>
}>

export function ShopListContent({
    productsList,
    priceCatalogList,
    searchRegex,
    filters,
    orderId,
    isRefundOrder,
    handleCloseMainDialog,
    userId,
    isEditMode,
    scrollContainerRef,
}: ShopListContentProps) {
    const [{ value: deliveries }, , deliveriesHelpers] = useField<DeliveryTimesField>(
        DynamicConfigKey.MULTIPLE_DELIVERIES,
    )
    const [{ value: priceListItem }] = useField<MeetingServicePriceListState>("priceList")

    const [showPriceWarning, setShowPriceWarning] = useState(false)
    const warnedRef = useRef(false)

    const priceListIncludeVat = !!priceListItem?.item?.priceListIncludeVat
    const priceCompareOptions = useMemo(() => ({ priceListIncludeVat }), [priceListIncludeVat])

    const priceById = useMemo(
        () => buildPriceMap(priceCatalogList, priceCompareOptions),
        [priceCatalogList, priceCompareOptions],
    )
    const selectedLines = useMemo(() => collectSelectedLines(deliveries), [deliveries])
    const changes = useMemo(
        () => collectPriceChanges(priceById, selectedLines, priceListItem?.item?.priceListId),
        [priceById, selectedLines, priceListItem?.item?.priceListId],
    )
    // Only a real price change opens the dialog; lines missing from the price list are listed as
    // context once it is open, so this does not start warning about orders we stayed silent about.
    const hasPriceChange = useMemo(
        () => changes.some(({ reason }) => reason === PriceChangeReason.PRICE_CHANGED),
        [changes],
    )

    useEffect(() => {
        if (warnedRef.current) return
        // a credit note corrects a booked order, so it never looks at the current price list
        if (isRefundOrder) return
        if (!priceCatalogList || priceCatalogList.length === 0) return
        if (selectedLines.length === 0) return
        if (!hasPriceChange) return

        warnedRef.current = true
        setShowPriceWarning(true)
    }, [isRefundOrder, priceCatalogList, selectedLines.length, hasPriceChange])

    const handleCloseDialog = () => {
        setShowPriceWarning(false)
    }

    const handleChangePrices = () => {
        if (!deliveries || !Array.isArray(deliveries.deliveryTimes)) {
            handleCloseDialog()
            return
        }

        const { updated, changed } = updatePricesInDeliveries(deliveries, priceById)
        if (changed) {
            deliveriesHelpers.setValue(updated)
        }
        handleCloseDialog()
    }

    return (
        <>
            {isRefundOrder ? (
                <CreditNoteOverlay orderId={orderId} />
            ) : (
                <ProductContainer
                    list={productsList}
                    searchRegex={searchRegex}
                    isListView={filters.isListView}
                    isFavoriteFilter={filters.isFavorite}
                    isRefundOrder={isRefundOrder}
                    userId={userId}
                    isEditMode={isEditMode}
                    scrollContainerRef={scrollContainerRef}
                />
            )}
            {showPriceWarning && (
                <PriceMismatchDialog
                    open={showPriceWarning}
                    handleCloseMainDialog={handleCloseMainDialog}
                    handleChangePrices={handleChangePrices}
                    changes={changes}
                    priceListName={priceListItem?.item?.priceListName}
                />
            )}
        </>
    )
}
